import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

const BIO = [
  "Jag heter Leila Dadgar och arbetar som terapeut, coach, utbildare och författare. Mitt arbete har under många år handlat om att möta människor i det som inte alltid syns utåt — tankar, känslor och mönster som påverkar livet på djupet.",
  "Genom mitt arbete har jag mött människor i olika livssituationer, men med en återkommande gemensam nämnare: en känsla av att ha tappat kontakten med sig själva. Många beskriver att de fungerar i vardagen, men att något saknas. Att de är där för andra, tar ansvar och gör det som förväntas, men samtidigt känner en inre tomhet, oro eller trötthet som inte riktigt går att förklara.",
  "Det är i dessa möten som min förståelse har fördjupats. Jag har sett hur mycket av det vi bär på formas av våra erfarenheter, våra relationer och de sätt vi lärt oss att hantera livet. Mönster som en gång varit nödvändiga kan senare bli det som begränsar oss, ofta utan att vi själva är medvetna om det.",
  "Parallellt med mitt arbete med klienter driver jag utbildningar inom terapi, coachning och personlig utveckling, där jag utbildar andra i att arbeta med människor på ett professionellt och medvetet sätt. I allt jag gör finns en vilja att göra det inre arbetet mer begripligt, tillgängligt och möjligt att faktiskt använda i vardagen.",
  "Som författare bär jag med mig samma förhållningssätt. Jag skriver inte för att ge snabba svar, utan för att hjälpa människor att förstå sig själva på djupet och skapa en förändring som håller över tid.",
];

const WHY = [
  "Den här metoden växte inte fram som en idé — den växte fram genom erfarenhet.",
  "Genom alla de samtal jag haft med människor började jag se tydliga mönster. Oavsett bakgrund, livssituation eller problematik återkom samma kärna: en bristande kontakt med det egna inre. Många visste vad de borde göra, men inte varför de kände som de gjorde eller varför de gång på gång hamnade i samma situationer.",
  "Det blev tydligt för mig att verklig förändring inte sker genom att bara förändra det yttre, utan genom att förstå det inre. Och att den förståelsen behöver ske steg för steg.",
  "Samtidigt har min egen resa spelat en viktig roll. Jag har, precis som många andra, fått lära mig vad det innebär att anpassa sig, att bära ansvar och att fortsätta framåt även när något inuti inte riktigt stämmer. Och jag vet hur lätt det är att tappa kontakten med sig själv i det.",
  "Det var i mötet mellan det professionella och det personliga som metoden tog form. En struktur började växa fram — inte som något påtvingat, utan som något som redan fanns där i de processer jag såg, både hos andra och i mig själv. En väg där förändring inte handlar om att bli någon annan, utan om att steg för steg komma tillbaka till sig själv.",
  "Metoden i tre faser för emotionell frihet är mitt sätt att göra den processen tydlig. Den hjälper dig att börja se, förstå och släppa det som påverkar dig, för att sedan kunna leva mer medvetet, närvarande och fritt.",
  "Det är inte en snabb lösning — det är en väg. Och för många är det just där förändringen börjar.",
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    if (user?.has_paid) navigate('/dashboard');
    else if (user) navigate('/betalning');
    else navigate('/registrera');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navigation />
      <div className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium text-[#A0655A] mb-4">
              Om mig
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium text-[#111111] leading-tight" data-testid="about-title">
              Leila Dadgar
            </h1>
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="space-y-5 text-[#111111]/65 leading-relaxed text-base mb-16">
            {BIO.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.div>

          <motion.div {...fadeUp(0.15)} className="mb-16">
            <div className="h-px bg-[#EBE3DF] mb-14" />
            <h2 className="heading-font text-2xl sm:text-3xl font-medium text-[#111111] mb-8 text-center">
              Varför jag skapade metoden
            </h2>
            <div className="space-y-5 text-[#111111]/65 leading-relaxed text-base">
              {WHY.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp(0.2)} className="text-center space-y-4">
            <div>
              <a
                href="https://leiladadgar.se"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#A0655A] hover:text-[#8B5549] text-sm font-medium transition-colors"
                data-testid="external-site-link"
              >
                Läs mer på leiladadgar.se
                <ChevronRight size={14} />
              </a>
            </div>
            {!user?.has_paid && (
              <div className="pt-6">
                <Button onClick={handleCTA}
                  className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-5 text-base"
                  data-testid="about-cta-btn">
                  {user ? 'Gå till betalning' : 'Börja din resa'}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
